import { observable, computed, action, reaction, runInAction } from 'mobx'
import TermsDialogState from 'components/TermsDialog/TermsDialogState'
import * as stripe from 'utils/stripe'
import { task } from 'mobx-task'
import sample from 'lodash/sample'
import { extractMessageFromError } from 'utils/errorUtil'
import memoize from 'memoizee'

const testimonials = [
  { quote: 'Kurt F. was great to work with and we will be using Taxfyle again for our 2017 taxes.', username: 'Matthew S' },
  { quote: 'Fantastic service and he saved me $1000! Thanks so much. ', username: 'Mayuka K' },
  { quote: 'The experience was quick and easy!  I\'ll do it again next year! John was great!', username: 'Terri M' },
  { quote: 'Freddie was fantastic! He was extremely responsive. He thoroughly answered all of my many questions. This process was made so much easier. Thank you Freddie and the taxfyle team for putting my mind at ease! I no longer have to worry if I\'m doing my taxes correctly. What an amazing app.', username: 'Haley A' },
  { quote: 'Gilbert was super helpful and did a great job of explaining everything clearly, thank you!', username: 'Jennifer A' },
  { quote: 'To the point advice, and directions. Very responsive and courteous. Definitely would recommend him.', username: 'Ruben A' },
  { quote: 'Joe knows what he\'s doing, very efficient service and overall experience! I will be coming back for future returns.', username: 'Jason T' },
  { quote: 'Thank you for the great turnaround time!', username: 'Vastal P' },
  { quote: 'This was my first time filing for a business.  Stewart was excellent with knowledge and wonderful with patience as he helped guide me through.  Thank you very much Taxfyle.', username: 'Gordon R' }
]

export default class FeeBreakdownState {
  stepId = 'FeeBreakdown'
  title = 'Checkout'
  trackingId = 'Onboarding - Fee Breakdown'

  @observable view = 'breakdown'
  @observable couponCode = ''
  @observable showingCouponField = false
  @observable hasAcceptedTerms = false
  @observable discountAmount = 0
  @observable couponsUsed = []
  @observable currentTestimonial = testimonials[0]
  @observable subtotal = 0
  @observable federalPrice = 0
  @observable statePrice = 0
  @observable adminFee = 0
  @observable orderTotal = 0
  @observable fees = []

  constructor ({ wizard }) {
    this.wizard = wizard
    this.termsDialog = new TermsDialogState()
    this.couponStore = wizard.rootStore.couponStore
    this.projectStore = wizard.rootStore.projectStore
    this.fetchPaymentMethods = this.fetchPaymentMethods.wrap(fn => memoize(fn, { promise: true, length: 0 }))
  }

  @computed get hideBackButton () {
    return this.submitJob.pending
  }

  @computed get years () {
    return this.wizard.getStep('TaxYears').selectedYears.map(x => x.year)
  }

  @computed get stateNames () {
    return this.wizard.getStep('TaxStates').selectedStates.map(x => x.name)
  }

  @computed get orderLines () {
    if (!this.legend) {
      return []
    }

    const federalPrice = this.federalPrice
    const statesPrice = this.statePrice
    return [
      { product: 'Federal', price: federalPrice },
      { product: 'State(s)', price: statesPrice },
      ...this.fees.map(x => ({ product: x.name, price: x.price }))
    ].filter(x => x !== false)
  }

  @computed get breakdownTitle () {
    // const type = this.legend
    // const years = this.wizard.getStep('TaxYears').selectedYears.map(x => x.year)
    // return type.name
    const typeStep = this.wizard.getStep('ProjectType')
    if (!typeStep.legend) {
      return 'Job Order'
    }

    return 'Job Order - ' + typeStep.legend.name
  }

  @computed get orderLinesDisplay () {
    return [
      ...this.orderLines,
      !!this.discountAmount && {
        dashed: true,
        product: 'Discount',
        price: this.discountAmount,
        isDiscount: true,
        bold: true
      }
    ].filter(x => x !== false)
  }

  @computed get amountDueNow () {
    return this.adminFee
  }

  @computed get legend () {
    return this.wizard.getStep('ProjectType').legend
  }

  @computed get answers () {
    return this.wizard.getStep('Questions').answers.map(x => x)
  }

  @computed get shouldShowFreeExtensionsInfo () {
    // Only show the info if they didn't already come here for free extensions.
    return !this.wizard.hereForFreeExtensions
  }

  @action setView (view) {
    this.view = view
  }

  showFeeBreakdown () {
    this.setView('breakdown')
  }

  showPaymentMethods () {
    this.setView('payment')
  }

  @action toggleCouponField () {
    this.showingCouponField = !this.showingCouponField
  }

  @action setCouponCode (code) {
    this.couponCode = code
  }

  @action async activate () {
    this.discountAmount = 0
    this.subtotal = 0
    this.federalPrice = 0
    this.statePrice = 0
    this.adminFee = 0
    this.orderTotal = 0
    this.fees = []
    this.fetchPaymentMethods()
    await Promise.all([
      this.wizard.syncJob(),
      this.couponStore.getCoupons()
    ])
    this.destroyPriceReaction = reaction(
      () => [this.stateNames, this.years, this.couponStore.coupons.map(x => x), this.legend],
      this.calculatePrice.bind(this),
      true
    )

    this.nextTestimonial()
    this.testimonialTimer = setInterval(this.nextTestimonial.bind(this), 7000)
  }

  @action nextTestimonial () {
    this.currentTestimonial = sample(testimonials)
  }

  @action deactivate () {
    if (this.destroyPriceReaction) {
      this.destroyPriceReaction()
      delete this.destroyPriceReaction
    }

    if (this.testimonialTimer) {
      clearInterval(this.testimonialTimer)
    }

    this.closeCheckout()
  }

  @task.resolved async applyCoupon () {
    if (!this.couponCode) {
      return
    }

    const result = await this.couponStore.addCoupon(this.couponCode)
    if (result) {
      this.setCouponCode('')
    }
  }

  @task async calculatePrice (price) {
    const result = await this.projectStore.calculatePrice(this.wizard.job.id)

    if (!result) {
      return
    }

    const allCoupons = this.couponStore.coupons
    runInAction(() => {
      this.subtotal = result.gross_amount
      this.discountAmount = result.coupon_amount
      this.federalPrice = result.federal_amount
      this.statePrice = result.state_amount
      this.adminFee = result.admin_fee_amount
      this.fees = result.fees
      this.orderTotal = result.total
      this.couponsUsed = result.coupon_codes.map(x => allCoupons.find(y => y.coupon.id === x))
        .filter(x => x)
        .filter(x => !x.coupon.expired)
        .map(x => x.coupon.friendlyName || x.coupon.id)
    })
  }

  @task async pay () {
    const accepted = await this.termsDialog.show()
    if (!accepted) {
      return
    }
    await this.fetchPaymentMethods()
    const billingStore = this.wizard.rootStore.billingStore
    if (billingStore.hasAnyPaymentMethods) {
      this.showPaymentMethods()
      return
    }
    const handler = this.checkoutHandler = stripe.checkout({
      token: (token) => {
        return this.saveCard(token)
      }
    })

    handler.open({
      currency: 'USD',
      name: 'Taxfyle',
      description: this.wizard.job.name,
      email: this.wizard.rootStore.authStore.user.email,
      amount: this.amountDueNow * 100
    })
  }

  closeCheckout () {
    if (this.checkoutHandler) {
      this.checkoutHandler.close()
    }

    this.checkoutHandler = null
  }

  @task.resolved saveCard (token) {
    const billingStore = this.wizard.rootStore.billingStore
    return billingStore.addCard(token).then((card) =>
      this.submitJob(card.id)
    )
  }

  @task.resolved async selectPaymentMethod (card) {
    await this.wizard.rootStore.billingStore.changeDefaultMethod(card)
    await this.submitJob(card.id)
  }

  @task fetchPaymentMethods () {
    const billingStore = this.wizard.rootStore.billingStore
    return Promise.all([
      billingStore.fetchCards(),
      billingStore.fetchCustomerRecord()
    ])
  }

  @task.resolved async submitJob (source) {
    const user = this.wizard.rootStore.authStore.user
    const job = this.wizard.job
    try {
      await this.wizard.rootStore.api.charges.create({
        amount: this.amountDueNow * 100,
        currency: 'usd',
        source,
        description: 'Taxfyle Service Fee',
        statement_descriptor: 'TAXFYLE',
        metadata: {
          job_id: this.wizard.job.id,
          user_id: user.id,
          email: user.email,
          first_name: user.firstName,
          last_name: user.lastName,
          full_name: user.displayName,
          job_status: job.status,
          payment_method: 'Card',
          platform_mode: process.env.NODE_ENV === 'production' ? 'LIVE' : 'TEST',
          platform_version: __VERSION__,
          platform: 'customer-portal',
          coupon_amount: this.discountAmount,
          coupons_used: this.couponsUsed.join(', '),
          filing_years: this.years.join(', '),
          work_states: this.stateNames.join(', ').substring(0, 500),
          fees: this.adminFee,
          state_total: this.statePrice,
          federal_total: this.federalPrice,
          subtotal: this.subtotal
        }
      })
      this.wizard.rootStore.projectStore.fetchProject.delete(this.wizard.job.id)
      await this.wizard.rootStore.projectStore.fetchProject(this.wizard.job.id)

      this.wizard.next()
    } catch (err) {
      this.wizard.rootStore.flashMessageStore.create({
        message: `An error occurred while submitting your job: ${extractMessageFromError(err)}`,
        type: 'error'
      })
      throw err
    }
  }
}



// WEBPACK FOOTER //
// ./src/client/screens/ProjectWizard/Steps/FeeBreakdown/FeeBreakdownState.js